import React, { useContext, useEffect, useState } from 'react';
import { copyToClipboard } from '../../util/clipboard';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import { Button, NamedLink, Page, BlogSkeleton } from '../../components';
import FooterContainer from '../FooterContainer/FooterContainer';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import css from './BlogContentPage.module.css';

import moment from 'moment';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useParams } from 'react-router-dom/cjs/react-router-dom.min';
import { Container } from '../../components/Layout/Layout';
import { LanguageContext } from '../../context/LanguageContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { useConfiguration } from '../../context/configurationContext';
import { createSlug } from '../../util/urlHelpers';
import {
  buildCloudinaryHeroImageSet,
  generateCategorySlug,
  generateCitySlug,
  parseCloudinaryDimensions,
} from '../../util/seo';
import { applyListingLanguage } from '../../util/listingLanguage';
import AboutAuthor from './AboutAuthor/AboutAuthor';
import { incrementBlogViewsAndClicks, requestFetchBlog } from './BlogContentPage.duck';

function htmlSafe(html) {
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
}

function hasWords(value) {
  if (!value) return false;
  const text = String(value).replace(/<[^>]*>/g, ' ').trim();
  return text.split(/\s+/).filter(Boolean).length > 0;
}

function getLocalizedBlog(rawBlog = {}, currentLanguage = 'en') {
  const localized = rawBlog?.translations?.[currentLanguage];

  if (!localized) {
    return rawBlog;
  }

  // Clone blog and replace top-level fields
  const { translations, ...baseBlog } = rawBlog;

  return {
    ...baseBlog,
    title: localized.title,
    label: localized.label,
    content: {
      ...rawBlog.content,
      ...localized.content,
    },
  };
}

function BlogContentPage() {
  const {
    blog: rawBlog,
    blogLoading,
    blogError,
    listing: rawListing,
    listingLoading,
    listingError,
  } = useSelector(state => state.BlogContentPage || {});

  const { locale } = useContext(LanguageContext);
  const blog = getLocalizedBlog(rawBlog, locale);
  const listing = applyListingLanguage(rawListing, locale);

  const dispatch = useDispatch();
  const { currentUser } = useSelector(state => state.user || {});
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const config = useConfiguration();
  const rootUrl = (config?.marketplaceRootURL || '').replace(/\/$/, '');
  const { blogId: blogIdParam } = useParams();

  const [canShare, setCanShare] = useState(false);
  useEffect(() => {
    setCanShare(true);
  }, []);

  // Increment view count client-side only — keeps SSR and bot crawls from inflating analytics.
  const blogId = rawBlog?._id || rawBlog?.id;
  useEffect(() => {
    if (blogId) {
      dispatch(incrementBlogViewsAndClicks(blogId, false));
    }
  }, [blogId]);

  const intl = useIntl();
  // Loading and error states (listing loads in parallel — do not block article body)
  if (blogLoading) {
    return (
      <Page
        title={intl.formatMessage({
          id: 'BlogContentPage.loadingTitle',
          defaultMessage: 'Loading...',
        })}
      >
        <TopbarContainer />
        <Container>
          <BlogSkeleton />
        </Container>
        <FooterContainer />
      </Page>
    );
  }
  if (blogError) {
    return (
      <Page
        title={intl.formatMessage({
          id: 'BlogContentPage.errorTitle',
          defaultMessage: 'Error',
        })}
      >
        <TopbarContainer />
        <div className={css.error}>
          <p>
            {blogError?.message ||
              listingError?.message ||
              intl.formatMessage({
                id: 'BlogContentPage.errorMessage',
                defaultMessage: 'Something went wrong.',
              })}
          </p>
          {blogError && blogIdParam && (
            <Button onClick={() => dispatch(requestFetchBlog(blogIdParam))}>
              <FormattedMessage id="BlogContentPage.retry" defaultMessage="Try again" />
            </Button>
          )}
        </div>
        <FooterContainer />
      </Page>
    );
  }

  if (!blog || (blog?.status === 'draft' && currentUser?.id?.uuid !== blog?.userId)) {
    return (
      <Page
        title={intl.formatMessage({
          id: 'BlogContentPage.noBlogTitle',
          defaultMessage: 'No Blog',
        })}
      >
        <TopbarContainer />
        <div className={css.error}>
          {intl.formatMessage({
            id: 'BlogContentPage.noBlogMessage',
            defaultMessage: 'No blog found.',
          })}
        </div>
        <FooterContainer />
      </Page>
    );
  };

  // Map API blog data to UI props
  const { title, label, image, content = {}, createdAt } = blog;
  const updatedAt = blog?.updatedAt || null;
  const {
    excerpt,
    time,
    readingTime,
    metaTags,
    body = {},
    conclusion,
  } = content;
  // Compose sections from body keys section1 ... sectionN
  const intro = body.introduction;
  const sections = Object.keys(body)
    .filter(k => k.startsWith('section'))
    .map(k => body[k]);

  // Use image.imageUrl if present
  const imageUrl   = image?.imageUrl || image?.secure_url;
  const { hero800, hero1600, preloadUrl } = buildCloudinaryHeroImageSet(imageUrl);
  const heroSrc = hero800 || imageUrl;
  const heroSrcSet =
    hero800 && hero1600 && hero800 !== imageUrl
      ? `${hero800} 800w, ${hero1600} 1600w`
      : undefined;
  const { width: imgWidth, height: imgHeight } = parseCloudinaryDimensions(heroSrc || imageUrl);
  const buildSocialImageUrl = (url) => {
    if (!url || typeof url !== 'string' || !url.includes('/upload/')) return url;
    return url.replace('/upload/', '/upload/w_1200,h_630,c_fill,f_auto,q_auto/');
  };
  const socialImageUrl = buildSocialImageUrl(imageUrl);
  const seoTags    = image?.seoTags || null;
  const imageAlt   = seoTags?.altText  || title || '';
  const imageTitle = seoTags?.titleTag || title || '';

  // Format date
  const dateStr = createdAt ? moment(createdAt).format('MMMM Do YYYY') : '';
  // Compose author (optional, not present in example)
  const author = listing?.author || null;
  // Use label as badge if present
  const badge = label ? <span className={css.category}>{label}</span> : null;

  const { id, attributes } = listing || {};
  const { title: listingTitle } = attributes || {};
  const listingPublicData = attributes?.publicData || {};
  const listingSlug = !!listingTitle?.trim() ? createSlug(listingTitle) : '';
  const listingCitySlug = generateCitySlug(listingPublicData?.city);
  const listingCategorySlug = generateCategorySlug(listingPublicData?.category);
  const showListingBar =
    listing && !listingLoading && !listingError && listing?.id?.uuid;

  // Custom share handlers
  const handleSocialShare = platform => {
    if (!canShare || typeof window === 'undefined') return;
    let url = '';
    const shareUrl = window.location.href;
    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedTitle = encodeURIComponent(listingTitle || title || '');
    switch (platform) {
      case 'facebook':
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
        break;
      case 'twitter':
        url = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`;
        break;
      case 'linkedin':
        url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
        break;
      case 'whatsapp':
        url = `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`;
        break;
      default:
        break;
    }
    window.open(url, '_blank', 'noopener,noreferrer,width=600,height=600');
  };

  const handleCopyToClipboard = async () => {
    if (!canShare || typeof window === 'undefined') return;
    try {
      const shareUrl = window.location.href;
      await copyToClipboard(shareUrl);
      alert(
        intl.formatMessage({
          id: 'BlogContentPage.copySuccess',
          defaultMessage: 'Link copied! You can now share it in your story, post, or bio.',
        })
      );
    } catch (err) {
      alert(
        intl.formatMessage({
          id: 'BlogContentPage.copyFail',
          defaultMessage: 'Failed to copy link. Please copy it manually.',
        })
      );
    }
  };

  const socialPlatforms = [
    {
      name: 'Facebook',
      icon: <i className="ri-facebook-box-line"></i>,
      handler: () => handleSocialShare('facebook'),
    },
    {
      name: 'Twitter',
      icon: <i className="ri-twitter-x-line"></i>,
      handler: () => handleSocialShare('twitter'),
    },
    {
      name: 'LinkedIn',
      icon: <i className="ri-linkedin-box-line"></i>,
      handler: () => handleSocialShare('linkedin'),
    },
    {
      name: 'WhatsApp',
      icon: <i className="ri-whatsapp-line"></i>,
      handler: () => handleSocialShare('whatsapp'),
    },
    {
      name: 'Instagram',
      icon: <i className="ri-instagram-line"></i>,
      handler: () => handleCopyToClipboard('Instagram'),
    },
    {
      name: 'TikTok',
      icon: <i className="ri-tiktok-line"></i>,
      handler: () => handleCopyToClipboard('TikTok'),
    },
  ];
  

  // Compose SEO/social props for blog
  const pageDescription = excerpt || '';
  const pageAuthor = author?.attributes?.profile?.displayName || author?.attributes?.profile?.publicData?.storeName || '';
  const authorProfile     = author?.attributes?.profile?.publicData || {};
  const authorStoreName   = authorProfile.storeName || pageAuthor;
  const authorUserId      = author?.id?.uuid;
  const authorCitySlug    = generateCitySlug(
    authorProfile?.storeAddress?.city || authorProfile?.city
  );
  const authorStoreSlug   = authorStoreName
    ? authorStoreName
        .toLowerCase()
        .replace(/'/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 60)
    : null;
  const authorStoreBase   = rootUrl && authorUserId && authorStoreSlug
    ? (authorCitySlug
        ? `${rootUrl}/rental-owner/${authorCitySlug}/${authorStoreSlug}/${authorUserId}`
        : `${rootUrl}/rental-owner/${authorStoreSlug}/${authorUserId}`)
    : null;
  const authorAtId        = authorStoreBase ? `${authorStoreBase}#local-business` : undefined;
  const marketplaceName = config?.marketplaceName || '';
  const marketplaceLogoUrl = `${rootUrl}/static/webapp-icon-1200x630.jpg`;
  const publisher = marketplaceName
    ? {
        '@type': 'Organization',
        name:    marketplaceName,
        logo: {
          '@type':    'ImageObject',
          url:         marketplaceLogoUrl,
          width:       1200,
          height:      630,
        },
      }
    : undefined;
  const imagesArr = imageUrl ? [imageUrl] : [];
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: title,
    description: pageDescription,
    image: imagesArr.length
      ? imagesArr.map(url => ({
          '@type':    'ImageObject',
          url,
          contentUrl: url,
          ...(seoTags?.altText  ? { description: seoTags.altText  } : {}),
          ...(seoTags?.caption  ? { caption:     seoTags.caption  } : {}),
          ...(imgWidth          ? { width:        imgWidth         } : {}),
          ...(imgHeight         ? { height:       imgHeight        } : {}),
        }))
      : [],
    author: {
      '@type': 'Organization',
      name:    pageAuthor,
      ...(authorAtId ? { '@id': authorAtId } : {}),
    },
    datePublished: createdAt,
    ...(updatedAt ? { dateModified: updatedAt } : {}),
    mainEntityOfPage: typeof window !== 'undefined' ? window.location.href : '',
    ...(publisher ? { publisher } : {}),
    inLanguage: 'en',
    ...(label ? { keywords: label } : {}),
    ...(label ? { articleSection: label } : {}),
  };

  const facebookImages = imagesArr.map(() => ({
    url:    socialImageUrl,
    width:  1200,
    height: 630,
    name:   'facebook',
    alt:    imageAlt,
  }));
  const twitterImages = imagesArr.map(img => {
    const regex = /w_(\d+),h_(\d+)/;
    const match = img.match(regex);
    const width = match ? parseInt(match[1], 10) : null;
    const height = match ? parseInt(match[2], 10) : null;
    return {
      url: img,
      width,
      height,
      name: 'twitter',
      alt: imageAlt,
    };
  });

  return (
    <Page
      title={title}
      description={pageDescription}
      author={pageAuthor}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      schema={schema}
      openGraphType="article"
      scrollingDisabled={false}
      className={css.page}
    >
      {preloadUrl ? (
        <Helmet>
          <link rel="preconnect" href="https://res.cloudinary.com" crossOrigin="" />
          <link rel="preload" as="image" href={preloadUrl} fetchpriority="high" />
        </Helmet>
      ) : null}
      <TopbarContainer />
      <Container className={css.containerWrapper}>
        {showListingBar && (
          <>
            <div className={css.alertBox}>
              <div className={css.iconAndText}>
                <span className={css.icon}>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M14 5.33333V13.9955C14 14.3667 13.7035 14.6667 13.3377 14.6667H2.66227C2.29663 14.6667 2 14.3707 2 14.0055V1.99453C2 1.63687 2.29913 1.33333 2.66814 1.33333H9.99787L14 5.33333ZM12.6667 6H9.33333V2.66666H3.33333V13.3333H12.6667V6ZM5.33333 4.66666H7.33333V6H5.33333V4.66666ZM5.33333 7.33333H10.6667V8.66666H5.33333V7.33333ZM5.33333 10H10.6667V11.3333H5.33333V10Z"
                      fill="#535862"
                    />
                  </svg>
                </span>
                <span className={css.text}>
                  <FormattedMessage
                    id="BlogContentPage.blogAboutListing"
                    defaultMessage="This blog is about: {listingTitle}"
                    values={{ listingTitle }}
                  />
                </span>
              </div>
              <NamedLink
                className={css.button}
                name="ListingPage"
                params={{
                  ...(listingCitySlug ? { city: listingCitySlug } : {}),
                  ...(listingCategorySlug ? { category: listingCategorySlug } : {}),
                  id: id.uuid,
                  slug: listingSlug
                }}
              >
                <FormattedMessage
                  id="BlogContentPage.viewListing"
                  defaultMessage="View Listing"
                />
              </NamedLink>
            </div>
          </>
        )}
        <div className={css.container}>
          <div className={css.cardContent}>
            {/* Blog context bar (e.g. related listing) */}
            <div className={css.header}>
              {badge}
              <h1 className={css.title}>{title}</h1>
            </div>
            {excerpt && <div className={css.excerpt}>{excerpt}</div>}
            <div className={css.meta}>
              <span>
                <i className="ri-time-line"></i> {dateStr}
              </span>

              {readingTime > 0 && <span>
                <i className="ri-book-open-line"></i> {readingTime}{' '}
                <FormattedMessage
                  id="BlogContentPage.minutesRead"
                  defaultMessage="minutes read"
                />
                </span>}
            </div>
            {imageUrl && (
              <div className={css.imageContainer}>
                <img
                  className={css.image}
                  src={heroSrc || imageUrl}
                  srcSet={heroSrcSet}
                  sizes="(min-width: 1024px) 800px, 100vw"
                  alt={imageAlt}
                  title={imageTitle}
                  loading="eager"
                  fetchpriority="high"
                  decoding="async"
                  {...(imgWidth  ? { width:  imgWidth  } : {})}
                  {...(imgHeight ? { height: imgHeight } : {})}
                />
                {seoTags?.caption && (
                  <figcaption className={css.lightboxCaption}>
                    {seoTags.caption}
                  </figcaption>
                )}
              </div>
            )}

            {intro && <div className={css.content}>{htmlSafe(intro)}</div>}
            {sections.map((section, idx) => (
              <div className={css.body} key={idx}>
                <div className={css.sectionTitle}>
                  {htmlSafe(section.heading)}
                </div>
                <div className={css.sectionBody}>
                  {htmlSafe(section.content)}
                </div>
              </div>
            ))}
            {hasWords(conclusion) && (
              <div className={css.conclusionBox}>{htmlSafe(conclusion)}</div>
            )}

            <div className={css.sharepostWrapper}>
              <span className={css.socialText}>
                <FormattedMessage
                  id="BlogContentPage.shareThisBlog"
                  defaultMessage="Share this blog"
                />
              </span>

              <div className={css.socialIconsRow}>
                <Button
                  className={css.socialIcon}
                  type="button"
                  variant={'secondary-outline'}
                  icon={<i className="ri-file-copy-line"></i>}
                  arrow={'left'}
                  style={{
                    gap: 8,
                  }}
                  onClick={handleCopyToClipboard}
                >
                  <FormattedMessage
                    id="BlogContentPage.copyLink"
                    defaultMessage="Copy link"
                  />
                </Button>
                {socialPlatforms.map((platform, index) => (
                  <Button
                    key={platform.name}
                    className={css.socialIcon}
                    aria-label={`Share on ${platform.name}`}
                    onClick={platform.handler}
                    type="button"
                    variant={'secondary-outline'}
                    icon={platform.icon}
                    arrow={'left'}
                  ></Button>
                ))}
              </div>
            </div>
          </div>
        </div>
        {author ? (
          <AboutAuthor
            history={history}
            routeConfiguration={routeConfiguration}
            profile={author}
            verified={true}
            intl={intl}
          />
        ) : null}
      </Container>
      <FooterContainer />
    </Page>
  );
}

export default BlogContentPage;
